import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { money } from '../../lib/money';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  DataTable,
  Drawer,
  EmptyState,
  FilterBar,
  Input,
  PageContainer,
  PageHeader,
  Pagination,
  Spinner,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { supplierReturnPath, purchaseOrderPath } from '../../lib/slug';
import { buildFilterParams } from '../../lib/filters';
import { suppliersApi } from '../suppliers/suppliersApi';
import { purchaseOrdersApi } from '../purchase-orders/purchaseOrdersApi';
import PoPickList from '../purchase-orders/PoPickList';
import {
  returnsApi,
  returnStatusMeta,
  creditNoteStatusMeta,
  returnReasonLabel,
  RETURN_STATUSES,
  RETURN_REASONS,
  CREDIT_NOTE_STATUSES,
} from './returnsApi';
import '../requisitions/requisitions.css';

const PAGE_SIZE = 25;
export default function ReturnsPage() {
  const navigate = useNavigate();
  const t = useT();
  const { activePropertyId, hasPermission } = useProperty();
  const canManage = hasPermission('returns.manage');

  const [data, setData] = useState({ items: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [values, setValues] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const [page, setPage] = useState(1);
  const debounceRef = useRef(null);
  const pickerDebounceRef = useRef(null);

  // "New return" entry point: pick a returnable PO (anything that has received goods).
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerPos, setPickerPos] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');

  const loadReturnablePos = useCallback(async (term) => {
    setPickerLoading(true);
    try {
      const res = await purchaseOrdersApi.list({
        statuses: 'PartiallyReceived,Closed',
        search: term || '',
        page: 1,
        pageSize: 50,
        sortBy: 'number',
        descending: true,
      });
      setPickerPos(res?.items || []);
    } catch {
      setPickerPos([]);
    } finally {
      setPickerLoading(false);
    }
  }, []);

  const openPicker = () => {
    setPickerOpen(true);
    setPickerSearch('');
    loadReturnablePos('');
  };

  const onPickerSearch = (value) => {
    setPickerSearch(value);
    if (pickerDebounceRef.current) clearTimeout(pickerDebounceRef.current);
    pickerDebounceRef.current = setTimeout(() => loadReturnablePos(value), 300);
  };

  const choosePo = (po) => {
    setPickerOpen(false);
    navigate(purchaseOrderPath(po, '/return'));
  };

  useEffect(() => {
    let active = true;
    suppliersApi.list({ page: 1, pageSize: 200, sortBy: 'name' })
      .then((res) => { if (active) setSuppliers(res?.items || []); }).catch(() => {});
    return () => { active = false; };
  }, [activePropertyId]);

  const fields = useMemo(() => [
    { id: 'statuses', label: t('Status'), type: 'multiselect', options: RETURN_STATUSES.map((s) => ({ value: s.value, label: t(s.label) })) },
    { id: 'reasons', label: t('Reason'), type: 'multiselect', options: RETURN_REASONS.map((r) => ({ ...r, label: t(r.label) })) },
    { id: 'creditNoteStatuses', label: t('Credit note'), type: 'multiselect', options: CREDIT_NOTE_STATUSES.map((s) => ({ value: s.value, label: t(s.label) })) },
    { id: 'supplierIds', label: t('Supplier'), type: 'multiselect', searchable: true, options: suppliers.map((s) => ({ value: s.id, label: s.name })) },
    { id: 'returned', label: t('Return date'), type: 'daterange', fromParam: 'returnedFrom', toParam: 'returnedTo' },
  ], [suppliers, t]);

  const filterParams = useMemo(() => buildFilterParams(fields, values), [fields, values]);
  const hasFilters = search !== '' || Object.keys(filterParams).length > 0;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await returnsApi.list({ page, pageSize: PAGE_SIZE, search, ...filterParams });
      setData(res);
    } catch (err) {
      setError(err?.message || t('Failed to load returns.'));
    } finally {
      setLoading(false);
    }
  }, [page, search, filterParams, t]);

  useEffect(() => { load(); }, [load]);

  const onSearchChange = (value) => {
    setSearchText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); setSearch(value); }, 300);
  };

  const onFilterChange = (id, value) => {
    setPage(1);
    setValues((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[id];
      else next[id] = value;
      return next;
    });
  };

  const open = (row) => navigate(supplierReturnPath(row));

  const columns = [
    { key: 'number', header: t('Return'), width: 130, render: (r) => <span style={{ fontFamily: 'var(--font-mono)' }}>{r.number || t('Draft')}</span> },
    {
      key: 'po', header: t('Purchase order'), width: 200,
      render: (r) => (
        <div>
          <div style={{ fontFamily: 'var(--font-mono)' }}>{r.purchaseOrderNumber || '—'}</div>
          <div className="pp-cell-muted" style={{ fontSize: 'var(--text-xs)' }}>{r.supplierName || t('No supplier')}</div>
        </div>
      ),
    },
    {
      key: 'status', header: t('Status'), width: 110,
      render: (r) => {
        const m = returnStatusMeta(r.status);
        return <Badge tone={m.tone} dot>{t(m.label)}</Badge>;
      },
    },
    { key: 'reason', header: t('Reason'), width: 130, render: (r) => t(returnReasonLabel(r.reason)) },
    {
      key: 'credit', header: t('Credit note'), width: 140,
      render: (r) => {
        const m = creditNoteStatusMeta(r.creditNoteStatus);
        return <Badge tone={m.tone}>{t(m.label)}</Badge>;
      },
    },
    { key: 'date', header: t('Returned'), width: 110, render: (r) => (r.returnDate ? new Date(r.returnDate).toLocaleDateString() : '—') },
    { key: 'lines', header: t('Lines'), width: 70, align: 'right', render: (r) => r.lineCount },
    { key: 'total', header: t('Value'), width: 140, align: 'right', render: (r) => money(r.total) },
  ];

  const totalPages = Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE));

  return (
    <PageContainer size="full">
      <PageHeader
        title={t('Returns')}
        description={t('Goods returned to suppliers (RMAs). Posting a return reverses stock, restores the purchase order and records a negative purchase entry; track the credit note alongside.')}
        icon={<Icon.ArrowUpCircle size={22} />}
        actions={canManage ? (
          <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={openPicker}>
            {t('New return')}
          </Button>
        ) : null}
      />

      <FilterBar
        searchPlaceholder={t('Search by return, PO, supplier or credit note…')}
        searchValue={searchText}
        onSearchChange={onSearchChange}
        fields={fields}
        values={values}
        onChange={onFilterChange}
      />

      {error ? (
        <EmptyState
          icon={<Icon.CircleAlert size={28} />}
          title={t("Couldn't load returns")}
          description={error}
          action={<Button variant="secondary" onClick={load}>{t('Retry')}</Button>}
        />
      ) : loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={data.items}
            rowKey={(r) => r.id}
            density="comfortable"
            onRowClick={open}
            emptyState={
              <EmptyState
                icon={<Icon.ArrowUpCircle size={28} />}
                title={hasFilters ? t('No matching returns') : t('No returns yet')}
                description={hasFilters ? t('Try adjusting your search or filters.') : t('Open a received purchase order and choose “Return goods”, or start one here.')}
                action={canManage && !hasFilters ? <Button variant="secondary" onClick={openPicker}>{t('New return')}</Button> : null}
              />
            }
          />
          {data.totalCount > 0 && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <Pagination page={page} pageCount={totalPages} onPageChange={setPage} totalItems={data.totalCount} pageSize={PAGE_SIZE} />
            </div>
          )}
        </>
      )}

      <Drawer
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title={t('Start a return')}
        description={t("Pick the purchase order you're returning against. Only orders that have received goods are shown.")}
        size="lg"
      >
        <div style={{ marginBottom: 'var(--space-3)' }}>
          <Input
            value={pickerSearch}
            onChange={(e) => onPickerSearch(e.target.value)}
            placeholder={t('Search by PO number or supplier…')}
            leadingIcon={<Icon.Search size={15} />}
            autoFocus
          />
        </div>
        {pickerLoading ? (
          <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-10)' }}><Spinner size={22} /></div>
        ) : pickerPos.length === 0 ? (
          <EmptyState
            icon={<Icon.ShoppingCart size={26} />}
            title={t('No orders with received goods')}
            description={pickerSearch ? t('No matching orders have received deliveries.') : t('Receive goods against a purchase order first, then you can return them here.')}
          />
        ) : (
          <PoPickList items={pickerPos} onPick={choosePo} showDelivery={false} />
        )}
      </Drawer>
    </PageContainer>
  );
}
